/* global com */
define((require) => {
    "use strict";

    const _             = require("underscore");
    const $             = require("jquery");
    const Class         = require("shared/util/Class");
    const EffectModel   = require("shared/ui/experience/action/effects/EffectModel");
    const TweenMax      = require("TweenMax");
    const TimelineMax   = require("TimelineMax");
    const Preconditions = require("shared/util/Preconditions");
    const Promise       = require("shared/util/Promise");
    const Browser       = require("shared/services/Browser");

    /**
     * The abstract base class for all effects.
     *
     * @class Effect
     */
    return Class.extend("Effect", {

        /**
         * @type {Backbone.View}
         */
        view: null,

        /**
         * The target element.
         *
         * @type {HTMLElement}
         */
        element: null,

        reversible: true,

        /**
         * @type {EffectModel}
         */
        model: null,

        /**
         * The GSAP instance.
         * @type {*}
         */
        _tween: null,

        _reverseDeferred: null,

        /**
         * Constructor.
         *
         * @param  {Backbone.View} view The associated view.
         * @param  {EffectModel} model   A concrete effect model.
         */
        constructor(view, model) {
            this.view = view;
            this.element = view.el;
            this.model = model;

            this.onInitialize();
            this._initializeTween();
        },

        /**
         * Starts playing the effect.
         */
        play() {
            this._tween.play();
        },

        /**
         * Pauses the effect.
         *
         * @param {*} atTime Optional, the time (or label for TimelineLite/TimelineMax instances) that
         * the instance should jump to before pausing (if none is defined, it will pause wherever the
         * playhead is currently located).
         */
        pause(atTime) {
            this._tween.pause(atTime);
        },

        stop() {
            this._tween.restart(true);
            this._tween.pause();
        },

        finish() {
            if (this.repeats()) {
                this.pause();

                return Promise.resolved();
            }

            return this.reverse();
        },

        /**
         * Plays the effect backwards, if supported.
         * @see #reversible
         */
        reverse() {
            if (this.reversible) {
                // If there should be any pending reverse animations, we make
                // sure to resolve it before overwriting it.
                if (this._reverseDeferred) {
                    this._reverseDeferred.resolve();
                }

                this._reverseDeferred = new $.Deferred();
                this._tween.reverse();

                return this._reverseDeferred.promise();
            }

            return Promise.resolved();
        },

        kill() {
            this._tween.kill();
        },

        /**
         * Whether the effect should repeat indefinitely.
         *
         * The default implementation returns true for 'loop' effects - i.e. triggerType is
         * equal to <tt>EffectModel.TRIGGER_TYPE_ATTENTION</tt>.
         *
         * @return {Boolean} <tt>true</tt> if the effect should repeat
         * indefinitely, <tt>false</tt> if not.
         */
        repeats() {
            return this.model.get("triggerType") === EffectModel.TRIGGER_TYPE_ATTENTION;
        },

        /**
         * Returns the configuration object passed to GSAP.
         *
         * Abstract method that must be overridden.
         *
         * @return {Object} The tween configuration, e.g. <tt>{opacity: 0.6}</tt>.
         */
        config() {
            Preconditions.abstractMethodError();
        },

        /**
         * Optional subclass callback for overwriting the default tween duration. Might be useful
         * when a specific <tt>triggerType</tt> needs a specific duration.
         *
         * By default, this method returns <tt>null</tt>.
         *
         * @param  {String} triggerType For convenience, passed <tt>this.model.get("triggerType")</tt>.
         * @return {Number}             The duration, in seconds, or <tt>null</tt> to use
         *                              the <tt>duration</tt> value in <tt>this.model</tt>.
         */
        // eslint-disable-next-line no-unused-vars
        duration(triggerType) {
            return null;
        },

        /**
         * Callback to be invoked at initialization time.
         */
        onInitialize() {},

        /**
         * Callback invoked every time the animation updates (on every frame while the animation is active).
         */
        onUpdate() {},

        _initializeTween() {
            if (this._tween) {
                this._tween.kill();
                this._tween = null;
            }

            const tweenConfig = this.config();
            const element = tweenConfig.element || this.element;
            const yoyo = _.isBoolean(tweenConfig.yoyo) ? tweenConfig.yoyo : true;
            // Determine duration
            const duration = this.duration(this.model.get("triggerType")) ||
                    tweenConfig.duration ||
                    this.model.get("duration") / 1000;
            // Need to adapt animations to the scaled widget size
            if (tweenConfig.scale && this.view.model.get("scale")) {
                tweenConfig.scale *= this.view.model.get("scale").x;
            }

            // No need to pass these to GSAP
            delete tweenConfig.element;
            delete tweenConfig.duration;
            delete tweenConfig.yoyo;

            const options = _.extend({
                force3D: Browser.capabilities.is3dAnimationsSupported, /* see http://api.greensock.com/js/ */
                paused: true,
                onUpdate: this.onUpdate.bind(this)
            }, tweenConfig);

            if (this.repeats()) {
                // Repeat indefinitely
                options.repeat = -1;

                this._tween = new TimelineMax(options);

                this._tween
                    .to(element, duration, _.extend({repeat: 1, yoyo}, tweenConfig))
                    // A dummy tween representing the pause/postDelay
                    // TODO: Is there a more official way of achieving this?
                    // Run looping/repeating effects with double speed to compensate for the reverse/yoyo time.
                    .to(element, (this.model.get("postDelay") / 1000) * 2, {})
                    .timeScale(2.0);
            } else {
                options.onReverseComplete = () => {
                    this._reverseDeferred.resolve();
                };

                this._tween = TweenMax.to(element, duration, options);
            }
        }

    }, {
        // Expose easing - see http://greensock.com/docs/#/HTML5/GSAP/Easing/
        Easing: com.greensock.easing
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/effects/Effect.js